/*
  NOWA sepet çekmecesi.

  Dawn'ın cart-drawer.js'inden bağımsız. Üç şeyi farklı yapıyor:

  1. TEK İSTEK. Shopify'ın Cart AJAX API'si gövdede `sections` parametresi kabul ediyor ve
     güncellenmiş sepetle BİRLİKTE render edilmiş section HTML'ini aynı yanıtta döndürüyor.
     Dawn önce mutasyonu yapıp sonra ayrı bir istekle section'ları çekiyor — iki gidiş-dönüş.

  2. İYİMSER ADET. +/- basıldığı anda rakam değişir, istek arkada gider. Yanıt gelince DOM
     sunucudan gelenle değiştirilir. Hata olursa eski hale döner.

  3. BİRLEŞTİRİLMİŞ TIKLAMALAR. Hızlıca 5 kez + basınca 5 istek gitmez; 300 ms içindeki
     tıklamalar tek bir "quantity = N" isteğine indirgenir.

  Erişilebilirlik: role="dialog" + aria-modal, odak tuzağı, Escape ile kapanma, açılışta
  odağı kapat butonuna alma, kapanışta odağı tetikleyen öğeye geri verme.
*/

const SECTION_ID = 'nowa-cart-drawer';
const BUBBLE_ID = 'cart-icon-bubble';

/*
  İkisi birden isteniyor çünkü `/cart/add.js` yanıtı `item_count` DÖNDÜRMÜYOR — yalnızca
  eklenen satırları döndürüyor. (`/cart/change.js` tam sepeti döndürür.) Rozeti taze
  section HTML'inden almak, sırf sayıyı öğrenmek için ikinci bir /cart.js isteği atmaktan
  ucuz: `sections` virgüllü liste kabul ediyor, ek gidiş-dönüş yok.
*/

/** Odaklanabilir öğeler — odak tuzağı için. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class NowaCartDrawer extends HTMLElement {
  /*
    Bölüm kimliği örnek başına. /cart sayfası aynı mantığı kullanıyor ama
    kendi bölümünü çizdiriyor; sabit bir kimlik olsaydı sayfa çekmecenin
    HTML'ini kendi içine basardı.
  */
  get sectionId() {
    return SECTION_ID;
  }

  get sections() {
    return `${this.sectionId},${BUBBLE_ID}`;
  }

  connectedCallback() {
    this.routes = window.Shopify?.routes?.root || '/';
    this.pending = new Map(); // lineKey -> { quantity, timer }
    this.bundleLocks = new Map(); // bundle -> devam eden senkron zinciri
    this.inFlight = 0;
    this.lastFocus = null;

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('change', this.onChange);

    // Sayfadaki her sepet bağlantısı drawer'ı açsın — /cart'a gitmek yerine.
    document.addEventListener('click', this.onDocumentClick, true);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick, true);
  }

  onDocumentClick = (event) => {
    const trigger = event.target.closest('a[href$="/cart"], a[href*="/cart?"], [data-nowa-cart-open]');
    if (!trigger) return;
    event.preventDefault();
    this.open(trigger);
  };

  onClick = (event) => {
    const { target } = event;

    /*
      Sepet değişirken kasaya gidilmesin. Görsel kilit (pointer-events) tek
      başına yetmiyor: buton klavyeyle de tetiklenebiliyor ve hızlı ödeme
      butonları ayrı elemanlar. Uçan istek veya bekleyen gecikmeli değişiklik
      varken ekrandaki sepet sunucudakiyle uyuşmuyor.
    */
    if (this.hasAttribute('data-busy') && target.closest('.nowa-drawer__ctas')) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    /*
      PAKET YENİDEN KURULURKEN SİLME KİLİTLİ.

      Eşik geçişi iki isteğe bölünmüş (önce adet, sonra alt kalemlerin
      eklenmesi) ve arada ~yarım saniye geçiyor. O aralıkta hediyeler henüz
      DOM'da yok; çöp kutusuna basılırsa `removeLine` kardeşleri o anki
      DOM'dan okuduğu için yalnızca ana satırı siliyor, arkadan gelen ekleme
      isteği hediyeleri yine de sepete koyuyordu. Sonuç: ana satırı olmayan
      "bedava" kalemler — müşteri hiç ürün almadan onlara para ödüyordu
      (ölçüldü: sepette ana satır yok, 6 bedava + 2 hediye, 355,20 zł).

      Kilit `data-busy`ye DEĞİL `inFlight`e bakıyor: `data-busy` gecikmeli
      birleştirme penceresini de kapsıyor ve ona bağlansaydı hızlı ardışık
      "+" tıklamaları da engellenirdi — oysa birleştirme tam olarak onun
      için var. Adet adımları da serbest; onlar `bundleLocks` zinciriyle
      sıraya giriyor ve her senkron satırı DOM'dan yeniden sorguluyor.
    */
    if (this.inFlight > 0 && target.closest('[data-nowa-line-remove]')?.closest('[data-bundle]')) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (target.closest('[data-nowa-drawer-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    const step = target.closest('[data-nowa-qty-step]');
    if (step) {
      event.preventDefault();
      const line = step.closest('[data-line-key]');
      const delta = Number(step.dataset.nowaQtyStep);
      this.stepQuantity(line, delta);
      return;
    }

    const remove = target.closest('[data-nowa-line-remove]');
    if (remove) {
      event.preventDefault();
      const line = remove.closest('[data-line-key]');
      this.removeLine(line);
      return;
    }

    const add = target.closest('[data-nowa-upsell-add]');
    if (add) {
      event.preventDefault();
      this.addVariant(add);
      return;
    }

    const arrow = target.closest('[data-nowa-upsell-scroll]');
    if (arrow) {
      event.preventDefault();
      this.upsellOtoDurdur();
      this.scrollUpsell(Number(arrow.dataset.nowaUpsellScroll));
    }
  };

  /** Teslimat sıklığı seçimi — paketin tamamını yeni plana çevirir. */
  onChange = (event) => {
    const select = event.target.closest('[data-nowa-plan-select]');
    if (!select) return;
    const line = select.closest('[data-line-key]');

    /*
      Plan değişimi paketi baştan kuruyor. Bekleyen bir adet değişikliği
      varken çalıştırılırsa paket grubunun adı değişiyor
      (`tier_2` → `tier_2@<planId>`) ve 350 ms sonra tetiklenen adet
      senkronu eski adla satırı arayıp bulamıyor, sessizce geri dönüyordu —
      müşterinin "+" tıklaması hiç uygulanmıyor, ekran da eski değere
      dönüyordu. Önce bekleyen adet yazılıyor, sonra plan değişiyor.
    */
    /*
      Paket yeniden kurulurken plan değiştirilemez: `switchPlan` kardeşleri
      o anki DOM'dan okuyor, eksik kalemler yeni gruba taşınmadan kalırdı.
      Seçici eski değerine döndürülüyor ki ekran sepetle uyuşsun.
    */
    if (this.inFlight > 0) {
      select.value = select.dataset.nowaCurrentPlan || '';
      return;
    }

    const bekleyen = this.pending.get(line?.dataset.lineKey);
    if (bekleyen) {
      clearTimeout(bekleyen.timer);
      this.pending.delete(line.dataset.lineKey);
      this.updateBusy();
      this.syncBundle(line, bekleyen.quantity).then(() => {
        const taze = this.querySelector(`[data-line-key="${CSS.escape(line.dataset.lineKey)}"]`);
        this.switchPlan(taze || line, select.value);
      });
      return;
    }

    this.switchPlan(line, select.value);
  };

  /** Enter ile form gönderimi de meşgulken durur. */
  onSubmit = (event) => {
    if (this.hasAttribute('data-busy')) event.preventDefault();
  };

  onKeydown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    // Odak tuzağı: Tab sırası panelin dışına çıkmasın.
    const items = [...this.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  open(trigger) {
    this.lastFocus = trigger || document.activeElement;
    this.setAttribute('open', '');
    document.body.classList.add('nowa-cart-open');
    // Panel açılırken odağı içeri al — ekran okuyucu arkadaki sayfada kalmasın.
    requestAnimationFrame(() => this.querySelector('[data-nowa-drawer-close]')?.focus());
    this.upsellOtoBaslat();
  }

  close() {
    this.upsellOtoDurdur();
    this.removeAttribute('open');
    document.body.classList.remove('nowa-cart-open');
    this.lastFocus?.focus?.();
    this.lastFocus = null;
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  /** İyimser adım: rakamı hemen değiştir, isteği birleştirerek gönder. */
  stepQuantity(line, delta) {
    if (!line) return;
    const valueEl = line.querySelector('[data-nowa-qty-value]');
    const current = this.pending.get(line.dataset.lineKey)?.quantity ?? Number(valueEl.textContent.trim());
    const next = Math.max(0, current + delta);

    valueEl.textContent = next;
    // 0'a inince satır kaybolacak; kullanıcıya "siliniyor" hissi ver.
    line.style.opacity = next === 0 ? '0.4' : '';

    if (line.dataset.bundleMin) {
      this.queueBundleSync(line, next);
      return;
    }

    this.setQuantity(line, next);
  }

  /*
    Paket eşiği. Adet `_min` değerine ulaştığında hediyeler sepete girer,
    altına düştüğünde çıkar. Hızlı tıklamada her adım için istek atmamak
    adına setQuantity ile aynı gecikme uygulanıyor.
  */
  queueBundleSync(line, quantity) {
    const key = line.dataset.lineKey;
    const existing = this.pending.get(key);
    if (existing?.timer) clearTimeout(existing.timer);

    const timer = setTimeout(() => {
      this.pending.delete(key);
      this.syncBundle(line, quantity);
    }, 350);

    this.pending.set(key, { quantity, timer });
    this.updateBusy();
  }

  /*
    Meşgul durumu. İki şeyi birden kapsıyor: uçan istek (`inFlight`) ve henüz
    gönderilmemiş gecikmeli değişiklik (`pending`). İkincisi de önemli —
    o aralıkta ekrandaki adet sunucudakiyle uyuşmuyor, kasaya gitmek eski
    sepetle ödeme başlatırdı.

    Nitelik host elemanda duruyor; drawer'ın içi her mutasyonda yeniden
    çizildiği için içerideki bir sınıf silinip giderdi.
  */
  updateBusy() {
    const busy = this.pending.size > 0 || this.inFlight > 0;
    if (busy) this.setAttribute('data-busy', '');
    else this.removeAttribute('data-busy');
  }

  /** Paketin alt kalemleri (bedava adetler, hediye ürünü). */
  /**
   * Bir satırın temsil ettiği TÜM sepet anahtarları.
   *
   * BXGY indirimi bedava adetleri birden çok satıra bölebiliyor; `nowa-cart-lines`
   * bunları tek satırda gösterip kalan anahtarları `data-extra-keys` ile taşıyor.
   * Sıfırlama yalnızca `data-line-key`e bakarsa bölünen parçalar sepette yetim
   * kalıyor — "GRATIS" satırı ekrandan siliniyor ama ücreti duruyordu.
   */
  lineKeys(el) {
    const anahtarlar = [el.dataset.lineKey];
    (el.dataset.extraKeys || '')
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k && k !== el.dataset.lineKey)
      .forEach((k) => anahtarlar.push(k));
    return anahtarlar;
  }

  bundleChildren(line) {
    const bundle = line.dataset.bundle;
    if (!bundle) return [];
    return [...this.querySelectorAll('[data-bundle]')].filter(
      (el) => el !== line && el.dataset.bundle.startsWith(`${bundle}-`)
    );
  }

  /**
   * Pakete eklenecek BEDAVA adetler.
   *
   * Hediyelerden ayrı üretiliyor: ikisinin eşiği farklı olabiliyor, dolayısıyla
   * biri hak edilirken diğeri edilmeyebilir (bkz. runBundleSync).
   */
  bundleFreeItems(line, freeQty) {
    const bundle = line.dataset.bundle;
    const plan = Number(line.dataset.sellingPlan) || null;
    const variantId = Number(line.dataset.variantId);
    if (!(freeQty > 0 && variantId)) return [];
    return [
      {
        id: variantId,
        quantity: freeQty,
        ...(plan ? { selling_plan: plan } : {}),
        properties: { _bundle: `${bundle}-free` },
      },
    ];
  }

  /**
   * Pakete eklenecek HEDİYE ÜRÜNLERİ.
   *
   * Birden fazla olabiliyor. Tek kimlik okunduğunda eşiğin altına düşüp geri
   * çıkan müşteri ikinci hediyesini kaybediyordu. Aynı kimlik iki kez
   * yazılmışsa tekilleniyor — yoksa Shopify aynı üründen iki satır açardı.
   *
   * Hediye ürünü plansız: plan yalnızca aynı ürünün bedava adetlerine uygulanır.
   */
  /**
   * Adede uyan teklif: `{ paid, free }`.
   *
   * Satır bütün kademeleri `_offers` içinde taşıyor (`ödenen:toplam` çiftleri).
   * O anki adede uyan EN YÜKSEK eşikli teklif seçiliyor: 3 adette "1+1",
   * 4 adette "4+6". Tek bir `_min/_free` çifti iki kademeyi anlatamıyordu —
   * hangisi yazılırsa diğerini alan müşteri yanlış paket alıyordu.
   *
   * Merdiven yoksa (bu güncellemeden önce oluşmuş sepetler) eski çifte
   * düşülüyor, davranış değişmiyor.
   */
  bundleOffer(line, quantity) {
    const merdiven = (line.dataset.bundleOffers || '')
      .split(',')
      .map((p) => p.split(':').map(Number))
      .filter(([paid, total]) => paid > 0 && total > paid)
      .sort((a, b) => a[0] - b[0]);

    let uygun = null;
    merdiven.forEach(([paid, total]) => {
      if (quantity >= paid) uygun = { paid, free: total - paid };
    });
    if (uygun) return uygun;

    return {
      paid: Number(line.dataset.bundleMin) || 0,
      free: Number(line.dataset.bundleFree) || 0,
    };
  }

  /**
   * Satırın hediye listesi: `[{ id, min }]`.
   *
   * Her hediyenin kendi eşiği var — kimi her adette, kimi yalnızca paket
   * eşiğinde veriliyor. Sabit iki alan yerine liste, çünkü her yeni hediye
   * aksi hâlde ayrı bir öznitelik ve ayrı bir kod yolu gerektiriyordu.
   */
  bundleGiftList(line) {
    const gorulen = new Set();
    return (line.dataset.bundleGifts || '')
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p) => {
        const [id, min] = p.split(':');
        return { id: Number(id), min: Math.max(1, Number(min) || 1) };
      })
      .filter((g) => g.id && !gorulen.has(g.id) && gorulen.add(g.id));
  }

  /** Verilen adette HAK EDİLEN hediyelerin sepet kalemleri. */
  bundleGiftItems(line, quantity) {
    const bundle = line.dataset.bundle;
    return this.bundleGiftList(line)
      .filter((g) => quantity >= g.min)
      .map((g) => ({ id: g.id, quantity: 1, properties: { _bundle: `${bundle}-gift` } }));
  }

  /*
    Aynı paket için senkronları SIRAYA sokar.

    Hata: 4'e çıkıp hediyeler eklenirken hemen 5'e basılınca ikinci senkron,
    ilkinin eklediği hediyeler henüz çizilmediği için "hediye yok" görüp bir
    daha ekliyordu — sepette 12 bedava sabun ve 2 e-kitap oluşuyordu.

    Zincir sayesinde ikinci senkron ilki bittikten sonra, taze DOM üzerinden
    çalışıyor. Satırı yeniden sorguluyoruz çünkü her çizimde eleman değişiyor.
  */
  syncBundle(line, quantity) {
    const bundle = line.dataset.bundle;
    if (!bundle) return Promise.resolve();

    const prev = this.bundleLocks.get(bundle) || Promise.resolve();
    const next = prev
      .catch(() => {})
      .then(() => this.runBundleSync(bundle, quantity));
    this.bundleLocks.set(bundle, next);
    return next;
  }

  /*
    Paketin tamamını tek seferlik <-> abonelik arasında çevirir.

    Satırın planını yerinde değiştirmek yerine paketi söküp yeniden kuruyoruz:
    plan değişince satır anahtarı da `_bundle` grup kimliği de değişiyor
    (grup kimliği plana göre benzersiz — iki paket birbirine karışmasın diye).
    Üç satırı tek tek düzenlemek üç ayrı istek ve yarım kalma riski demekti.

    Hediye ürünü (e-kitap) plansız kalıyor — abonelik planı o ürüne tanımlı değil.
  */
  async switchPlan(line, planId) {
    if (!line || this.planSwitching) return;
    const bundle = line.dataset.bundle;
    if (!bundle) return;

    const children = this.bundleChildren(line);

    /*
      Bedava adet içeren paket aboneliğe ÇEVRİLEMEZ: mağaza indirimleri
      abonelik satırlarına uygulanmadığı için o adetler plan fiyatından
      faturalanır ve "Gratis do zestawu" yazan satır 355,20 zł olurdu.
      Seçici bu planları zaten listelemiyor; bu, doğrudan gelen isteğe karşı
      son kapı. `_subok` mağaza tarafında abonelik indirimi kurulduğunu
      söylüyorsa kapı açılıyor.
    */
    const bedavaVar = children.some((el) => el.dataset.bundle.endsWith('-free'));
    if (planId && bedavaVar && line.dataset.subOk !== '1') {
      await this.hardRefresh();
      return;
    }

    this.planSwitching = true;
    line.dataset.busy = 'true';

    try {
      const taban = bundle.split('@')[0];
      const yeniGrup = planId ? `${taban}@${planId}` : taban;
      const eskiPlan = line.dataset.sellingPlan || '';

      // Ana satır: ürün sayfasından gelen paket kuralları aynen korunmalı,
      // yoksa eşik mantığı ve "aboneliğe geç" seçeneği çevirmeden sonra kaybolur.
      const kural = (ad, deger) => (deger ? { [ad]: deger } : {});

      /*
        Paketi hedef plana göre yeniden kurar. Geri alma da aynı üreticiyi
        kullanıyor: ekleme yarıda kalırsa müşteri paketini kaybetmesin diye
        eski hâli birebir aynı biçimde geri kuruluyor.
      */
      const paketKur = (plan) => {
        const grup = plan ? `${taban}@${plan}` : taban;
        const items = [
          {
            id: Number(line.dataset.variantId),
            quantity: Number(line.dataset.quantity) || 1,
            ...(plan ? { selling_plan: Number(plan) } : {}),
            properties: {
              _bundle: grup,
              ...kural('_min', line.dataset.bundleMin),
              ...kural('_free', line.dataset.bundleFree),
              ...kural('_gifts', line.dataset.bundleGifts),
              ...kural('_offers', line.dataset.bundleOffers),
              ...kural('_plan', line.dataset.planOffer),
              ...kural('_subok', line.dataset.subOk),
            },
          },
        ];

        children.forEach((el) => {
          const ek = el.dataset.bundle.endsWith('-gift') ? 'gift' : 'free';
          items.push({
            id: Number(el.dataset.variantId),
            quantity: Number(el.dataset.quantity) || 1,
            // Hediye ürünü plansız — plan yalnızca aynı ürünün bedava adetlerine uygulanır
            ...(plan && ek === 'free' ? { selling_plan: Number(plan) } : {}),
            properties: { _bundle: `${grup}-${ek}` },
          });
        });

        // Shopify tek istekte eklenen kalemleri ters diziyor
        return items.reverse();
      };

      /*
        Sıfırlanacak anahtarlar: hem ana satırın hem alt kalemlerin TÜM
        parçaları. İndirim motoru aynı varyantın satırlarını bölebiliyor;
        yalnızca `data-line-key` sıfırlansaydı kalan parçalar sepette yetim
        kalırdı — ölçüldü: aboneliğe geçince 2 adetlik bir GRATIS parçası
        sepette kalıyor, ekranda hiç görünmüyor ama 148,00 zł tahsil
        ediliyordu.
      */
      const updates = {};
      this.lineKeys(line).forEach((k) => {
        updates[k] = 0;
      });
      children.forEach((el) => {
        this.lineKeys(el).forEach((k) => {
          updates[k] = 0;
        });
      });

      const silindi = await this.mutate('cart/update.js', { updates }, { render: false });
      if (!silindi) return;

      const eklendi = await this.mutate('cart/add.js', { items: paketKur(planId) });
      if (!eklendi) {
        // Ekleme başarısız: paket sepetten silinmiş durumda kalmasın
        await this.mutate('cart/add.js', { items: paketKur(eskiPlan) });
        return;
      }

      /*
        Tek seferliğe dönen paket eşiği sağlıyorsa hediyeleri geri kazanır.
        Abonelikte 4 adede çıkıp sonra "Jednorazowo"ya dönen müşteri, adet
        değişmediği için eşik kontrolünü hiç tetiklemiyor ve hak ettiği
        6 GRATIS + e-kitabı alamıyordu.
      */
      if (!planId) {
        const yeni = this.querySelector(
          `[data-bundle="${CSS.escape(yeniGrup)}"][data-bundle-min]`
        );
        if (yeni && !this.bundleChildren(yeni).length) {
          await this.syncBundle(yeni, Number(yeni.dataset.quantity) || 0);
        }
      }
    } finally {
      this.planSwitching = false;
    }
  }

  /*
    Ana satırın adedini yazar. Satır birden çok sepet kalemine bölünmüşse
    (indirim motoru aynı varyantı parçalayabiliyor) adet İLK anahtara
    yazılıyor, kalan parçalar sıfırlanıyor — yoksa 4'ten 7'ye çıkarken sepette
    3 + 4 diye iki ayrı paket satırı oluşuyordu.
  */
  anaAdetYaz(line, quantity, updates = {}) {
    this.lineKeys(line).forEach((key, i) => {
      updates[key] = i === 0 ? quantity : 0;
    });
    return updates;
  }

  async runBundleSync(bundle, quantity) {
    const line = this.querySelector(`[data-bundle="${CSS.escape(bundle)}"][data-bundle-min]`);
    if (!line) return;

    /*
      Eşik ve bedava adet o anki ADETE göre seçiliyor — sabit değil.
      İki promosyon kademesi varken doğru olan tek yol bu.
    */
    const teklif = this.bundleOffer(line, quantity);
    const min = teklif.paid;
    const beklenenBedava = teklif.free;

    /*
      HER HEDİYENİN KENDİ EŞİĞİ VAR — bedava adet eşiğinden bağımsız.

      Önce tek eşik vardı ve hediyeler de ona bağlıydı. Hediyeyi KENDİSİ veren
      bir kademe ("Kup 1", 1 adet) satın alındığında satıra yine teklifin eşiği
      (`_min` = 4) yazılıyor; müşteri çekmecede 1'den 2'ye çıkınca kod "paket
      hak edilmedi" deyip bütün alt kalemleri siliyordu.

      Şimdi her hediye ayrı değerlendiriliyor: woreczek ve pas eşik 1 (her
      adette durur), e-kitap eşik 4 (4'e çıkınca gelir, altına düşünce gider).
      Eksik olan eklenir, hak edilmeyen silinir — ikisi aynı geçişte olabilir.
    */
    const children = this.bundleChildren(line);
    const bedavalar = children.filter((el) => el.dataset.bundle.endsWith('-free'));
    const hediyeler = children.filter((el) => el.dataset.bundle.endsWith('-gift'));
    const mevcutHediyeler = new Set(hediyeler.map((el) => Number(el.dataset.variantId)));

    const bedavaHak = quantity > 0 && quantity >= min;
    const giftList = this.bundleGiftList(line);
    const hakEdilen = new Set(
      giftList.filter((g) => quantity > 0 && quantity >= g.min).map((g) => g.id)
    );

    /*
      ABONELİKTE HEDİYE EKLENMEZ.

      Mağazanın otomatik indirimleri abonelik satırlarını görmüyor (ölçüldü),
      yani "bedava" adetler plan fiyatından faturalanırdı: aboneliğe 1 adet
      ekleyip sepette 4'e çıkaran müşteriye 6 sabun + e-kitap ekleniyor ve
      236,80 zł yerine 602,00 zł çıkıyordu. Abonelikte yalnızca adet değişir.

      `_subok` (kademe ayarı) mağazada aboneliğe de uygulanan bir indirim
      kurulduğunu söylüyorsa hediyeler yine ekleniyor.

      Bu yalnızca EKLEMEYİ engelliyor; hak edilmeyen kalemlerin silinmesi
      abonelikte de çalışmalı, yoksa ödenmiş görünen bir kalem sepette kalırdı.
    */
    const planli = Boolean(line.dataset.sellingPlan);
    const abonelikEngeli = planli && line.dataset.subOk !== '1';

    const updates = this.anaAdetYaz(line, quantity);
    const sifirla = (el) => {
      this.lineKeys(el).forEach((k) => {
        updates[k] = 0;
      });
      el.style.opacity = '0.4';
    };
    /*
      Mevcut bedava adet beklenenden FARKLIYSA düzeltiliyor. Önce yalnızca
      "hiç yoksa ekle" vardı; kademe atlayan müşteride (1+1 alıp 4'e çıkan)
      sepette 1 bedava kalıyor, 6'ya hiç tamamlanmıyordu. Eski satırlar
      sıfırlanıp doğru adetle tek satır ekleniyor.
    */
    const mevcutBedava = bedavalar.reduce(
      (t, el) => t + (Number(el.dataset.quantity) || 0),
      0
    );
    const bedavaYenilensin = bedavaHak && mevcutBedava !== beklenenBedava;

    if (!bedavaHak || bedavaYenilensin) bedavalar.forEach(sifirla);
    // hak edilmeyen hediyeler tek tek siliniyor — hepsi ya hiçbiri değil
    hediyeler
      .filter((el) => !hakEdilen.has(Number(el.dataset.variantId)))
      .forEach(sifirla);

    const eklenecek = [];
    if (!abonelikEngeli) {
      if (bedavaHak && (!bedavalar.length || bedavaYenilensin)) {
        eklenecek.push(...this.bundleFreeItems(line, beklenenBedava));
      }
      /*
        Yalnızca EKSİK olanlar ekleniyor. "Hiç çocuk yoksa hepsini ekle"
        denseydi, bir hediye bir sebeple düşmüşse (stok, kasada düzenleme)
        diğerleri durduğu için bir daha hiç geri gelmezdi.
      */
      this.bundleGiftItems(line, quantity)
        .filter((it) => !mevcutHediyeler.has(it.id))
        .forEach((it) => eklenecek.push(it));
    }

    // Eklenecek bir şey yok: tek istek yeter
    if (!eklenecek.length) {
      await this.mutate('cart/update.js', { updates });
      return;
    }

    /*
      add.js mevcut satırın adedini değiştiremiyor, update.js de yeni satır
      açamıyor — iki istek şart. İlkini çizdirmeden geçiyoruz ki eksik
      kalemli bir ara kare görünmesin.

      Shopify tek istekte eklenen kalemleri ters diziyor; görünüm sırası
      korunsun diye gönderirken çeviriyoruz.
    */
    /*
      İlk isteğin SONUCU kontrol ediliyor. Kontrol edilmediğinde adet
      güncellemesi hata alsa bile (stok, ağ) ekleme isteği yine gidiyor ve
      müşteri eşiği sağlamadan bedava adetleri + hediyeleri alıyordu.
      `mutate` hata durumunda zaten sepeti tazeliyor; burada durmak yeterli.
    */
    const yazildi = await this.mutate('cart/update.js', { updates }, { render: false });
    if (yazildi === false) return;
    await this.mutate('cart/add.js', { items: eklenecek.reverse() });
  }

  /**
   * Satırı siler. Paketin ücretli satırı gidiyorsa ona bağlı bedava satırı da
   * götürür — yoksa sepette karşılığı olmayan bir "GRATIS" kalemi yetim kalır.
   * İkisi tek `update.js` isteğinde sıfırlanıyor, ara kare oluşmuyor.
   */
  removeLine(line) {
    if (!line) return;
    const bundle = line.dataset.bundle;
    // Paketin tüm alt kalemleri: `-free` bedava adetler, `-gift` ayrı hediye ürünü
    const siblings = bundle
      ? [...this.querySelectorAll('[data-bundle]')].filter(
          (el) => el !== line && el.dataset.bundle.startsWith(`${bundle}-`)
        )
      : [];

    if (!siblings.length) {
      this.setQuantity(line, 0, { immediate: true });
      return;
    }

    const updates = {};
    this.lineKeys(line).forEach((k) => {
      updates[k] = 0;
    });
    siblings.forEach((el) => {
      this.lineKeys(el).forEach((k) => {
        updates[k] = 0;
      });
      el.style.opacity = '0.4';
    });
    line.dataset.busy = 'true';
    this.mutate('cart/update.js', { updates });
  }

  setQuantity(line, quantity, { immediate = false } = {}) {
    const key = line.dataset.lineKey;
    const existing = this.pending.get(key);
    if (existing?.timer) clearTimeout(existing.timer);

    const send = () => {
      this.pending.delete(key);
      /*
        Satır kilidi SADECE silmede. Adet değişiminde de kilitlenince CSS'teki
        `pointer-events: none` yüzünden istek uçarken (~300-600ms) artı tuşuna
        basılan tıklamalar sessizce yutuluyordu: yarım saniye aralıklarla üç kez
        basan kullanıcı 4 yerine 3 görüyordu. Adet için zaten iyimser güncelleme
        ve gecikmeli birleştirme var; kilide gerek yok.
      */
      if (quantity === 0) line.dataset.busy = 'true';
      /*
        Bölünmüş satır: `change.js` tek bir anahtarı güncelliyor, diğer
        parçalar olduğu gibi kalıyordu. Parça varsa toplu güncelleme
        kullanılıyor — adet ilk anahtara yazılıyor, kalanlar sıfırlanıyor.
      */
      if (this.lineKeys(line).length > 1) {
        this.mutate('cart/update.js', { updates: this.anaAdetYaz(line, quantity) });
        return;
      }
      this.mutate('cart/change.js', { id: key, quantity });
    };

    if (immediate) {
      send();
      return;
    }
    this.pending.set(key, { quantity, timer: setTimeout(send, 300) });
    this.updateBusy();
  }

  async addVariant(button) {
    const id = Number(button.dataset.variantId);
    if (!id) return;
    const label = button.textContent;
    button.disabled = true;
    button.textContent = '…';
    const ok = await this.mutate('cart/add.js', { items: [{ id, quantity: 1 }] });
    if (!ok) {
      button.disabled = false;
      button.textContent = label;
    }
  }

  /**
   * Sepeti değiştirir ve dönen section HTML'i ile arayüzü tazeler.
   * `sections` parametresi sayesinde tek gidiş-dönüş yeter.
   */
  async mutate(endpoint, payload, { render = true } = {}) {
    this.setError('');
    this.inFlight += 1;
    this.updateBusy();
    try {
      const res = await fetch(`${this.routes}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...payload,
          sections: this.sections,
          sections_url: window.location.pathname,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'cart error');

      if (render) this.applySections(data.sections);
      document.dispatchEvent(new CustomEvent('nowa:cart:updated', { detail: data }));
      return true;
    } catch (error) {
      console.error('[nowa-cart]', error);
      this.setError(error.message);
      // Sunucudan taze HTML çekip iyimser değişikliği geri al.
      await this.hardRefresh();
      return false;
    } finally {
      this.inFlight -= 1;
      this.updateBusy();
    }
  }

  /** Yanıttaki section'ları uygula: drawer gövdesi + sepet rozeti. */
  applySections(sections) {
    if (!sections) return;
    this.render(sections[this.sectionId]);

    /*
      Çizim sonrası track YENİ bir eleman: eski dinleyiciler ölü kalıyor,
      sayaç da artık DOM'da olmayan bir öğeyi kaydırmaya çalışıyordu.
      Panel açıksa otomatik dönüş taze track üzerinde yeniden kuruluyor.
    */
    if (this.isOpen) this.upsellOtoBaslat();

    const bubbleHtml = sections[BUBBLE_ID];
    if (bubbleHtml) {
      const icon = document.querySelector('#cart-icon-bubble');
      if (icon) icon.innerHTML = bubbleHtml;
    }
  }

  /** Section HTML'ini panelin içine uygula, kaydırma konumunu koru. */
  render(html) {
    if (!html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.querySelector('[data-nowa-drawer-inner]');
    const current = this.querySelector('[data-nowa-drawer-inner]');
    if (!fresh || !current) return;

    const body = current.querySelector('.nowa-drawer__body');
    const scrollTop = body?.scrollTop ?? 0;

    current.replaceWith(fresh);

    const newBody = this.querySelector('.nowa-drawer__body');
    if (newBody) newBody.scrollTop = scrollTop;

    this.restorePending();
    this.refreshArrows();
  }

  /*
    Yeniden çizim, henüz gönderilmemiş iyimser adetleri ezmemeli.

    Hata şuydu: kullanıcı yarım saniye aralıklarla + tuşuna basınca, ilk isteğin
    yanıtı gelip paneli yeniden çiziyor ve ekranı SUNUCUDAKİ eski değere geri
    alıyordu. Sonraki tıklama o eski değeri okuyup üstüne sayıyor, aradaki bir
    artış kayboluyordu (1'den üç kez basınca 4 yerine 3 çıkıyordu).

    Çizimden sonra bekleyen değerleri taze DOM'a geri yazıyoruz.
  */
  restorePending() {
    this.pending.forEach(({ quantity }, key) => {
      const line = this.querySelector(`[data-line-key="${CSS.escape(key)}"]`);
      const valueEl = line?.querySelector('[data-nowa-qty-value]');
      if (valueEl) valueEl.textContent = quantity;
    });
  }

  async hardRefresh() {
    try {
      const res = await fetch(`${this.routes}?sections=${this.sections}`);
      this.applySections(await res.json());
    } catch (error) {
      console.error('[nowa-cart] tazeleme başarısız', error);
    }
  }

  setError(message) {
    const box = this.querySelector('[data-nowa-drawer-error]');
    if (box) box.textContent = message || '';
  }

  scrollUpsell(direction, { dongu = false } = {}) {
    const track = this.querySelector('[data-nowa-upsell-track]');
    if (!track) return;
    const card = track.firstElementChild;
    const step = card ? card.getBoundingClientRect().width + 12 : 240;

    /*
      Otomatik dönüşte sona gelince başa sarılıyor; elle oklarda sarılmıyor
      çünkü orada niyet açık ("ileri") ve beklenmedik bir sıçrama olurdu.
      1px tolerans: tarayıcılar kesirli kaydırma değerleri üretiyor.
    */
    if (dongu && direction > 0 && track.scrollLeft + track.clientWidth >= track.scrollWidth - 1) {
      track.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }
    track.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  /*
    OTOMATİK DÖNÜŞ.

    Yalnızca çekmece AÇIKKEN çalışıyor: kapalıyken zamanlayıcı döndürmek
    hem boşuna hem de açılışta kartların rastgele bir yerden başlamasına
    yol açıyordu.

    Ziyaretçi kendisi kaydırır, oka basar veya kartın üstüne gelirse
    duruyor ve bir daha başlamıyor — kendi seçimini yaptığı anda karusel
    onu takip etmeyi bırakmalı, elinden içerik kaçırmamalı.

    `prefers-reduced-motion` açıksa hiç başlamıyor.
  */
  upsellOtoBaslat() {
    this.upsellOtoDurdur();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const track = this.querySelector('[data-nowa-upsell-track]');
    if (!track) return;
    // Tek kart varsa döndürecek bir şey yok
    if (track.scrollWidth <= track.clientWidth + 1) return;

    this.upsellDur = () => this.upsellOtoDurdur();
    ['pointerdown', 'wheel', 'touchstart', 'mouseenter'].forEach((olay) =>
      track.addEventListener(olay, this.upsellDur, { passive: true, once: true })
    );

    this.upsellSayaci = setInterval(() => {
      if (!this.isOpen) return;
      this.scrollUpsell(1, { dongu: true });
    }, 4000);
  }

  upsellOtoDurdur() {
    if (this.upsellSayaci) {
      clearInterval(this.upsellSayaci);
      this.upsellSayaci = null;
    }
    const track = this.querySelector('[data-nowa-upsell-track]');
    if (track && this.upsellDur) {
      ['pointerdown', 'wheel', 'touchstart', 'mouseenter'].forEach((olay) =>
        track.removeEventListener(olay, this.upsellDur)
      );
    }
  }

  refreshArrows() {
    const track = this.querySelector('[data-nowa-upsell-track]');
    if (!track) return;
    const update = () => {
      const prev = this.querySelector('[data-nowa-upsell-scroll="-1"]');
      const next = this.querySelector('[data-nowa-upsell-scroll="1"]');
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };
    track.addEventListener('scroll', update, { passive: true });
    update();
  }
}

/*
  /cart sayfası. Çekmeceyle AYNI etkileşim mantığını kullanıyor: adet, silme,
  paket eşiği, plan seçimi, upsell. Farkı yalnızca kabuk — açılıp kapanmıyor,
  örtüsü ve odak tuzağı yok, kendi bölümünü çizdiriyor.

  Miras almak kopyalamaya tercih edildi: sepet mantığı iki yerde yaşasaydı
  biri düzeltilip diğeri geride kalırdı.
*/
class NowaCartPage extends NowaCartDrawer {
  /*
    Bölüm kimliği ŞABLONDAKİ anahtar, dosya adı değil. Çekmece layout'ta
    `{% section %}` ile çizildiği için kimliği dosya adına eşit; sepet sayfası
    ise templates/cart.json içinde "cart" anahtarıyla duruyor. Dosya adını
    gönderince Shopify o bölümü döndürmüyor ve sayfa sessizce eskide kalıyordu:
    sepet rozeti güncelleniyor, satırlar donuyordu.
  */
  get sectionId() {
    return this.dataset.sectionId || 'nowa-cart-page';
  }

  connectedCallback() {
    this.routes = window.Shopify?.routes?.root || '/';
    this.pending = new Map();
    this.bundleLocks = new Map();
    this.inFlight = 0;

    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('submit', this.onSubmit);
    // Belge geneli tıklama yakalayıcı YOK — sepet bağlantısı zaten bu sayfa
  }

  disconnectedCallback() {}

  // Sayfa açılıp kapanmıyor
  open() {}
  close() {}
}

if (!customElements.get('nowa-cart-page')) {
  customElements.define('nowa-cart-page', NowaCartPage);
}

if (!customElements.get('nowa-cart-drawer')) {
  customElements.define('nowa-cart-drawer', NowaCartDrawer);
}

/*
  Ürün sayfasındaki "Dodaj do koszyka" ve benzeri yerler bu olayı yayınlar; drawer
  kendini tazeleyip açılır. Böylece sepete ekleyen kod drawer'ın iç yapısını bilmek
  zorunda kalmaz.
*/
document.addEventListener('nowa:cart:open', async (event) => {
  const drawer = document.querySelector('nowa-cart-drawer');
  if (!drawer) return;
  if (event.detail?.sections?.[SECTION_ID]) {
    drawer.applySections(event.detail.sections);
  } else {
    await drawer.hardRefresh();
  }
  drawer.open(event.detail?.trigger);
});
